import { z } from 'zod'

const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/

export const patientFormSchema = z.object({
  firstName: z.string().min(1, 'patients:errors.firstNameRequired').max(100),
  middleName: z.string().max(100).optional().or(z.literal('')),
  lastName: z.string().min(1, 'patients:errors.lastNameRequired').max(100),
  egn: z.string().length(10, 'patients:errors.egnLength'),
  dateOfBirth: z.string().min(1, 'patients:errors.dateOfBirthRequired'),
  gender: z.enum(['MALE', 'FEMALE'], { message: 'patients:errors.genderRequired' }),
  placeOfBirth: z.string().max(255).optional().or(z.literal('')),

  idCardNumber: z.string().max(20).optional().or(z.literal('')),
  idCardIssuedBy: z.string().max(100).optional().or(z.literal('')),
  idCardIssuedDate: z.string().optional().or(z.literal('')),

  phone: z.string().max(20).optional().or(z.literal('')),
  address: z.string().max(500).optional().or(z.literal('')),
  addressOblast: z.string().max(100).optional().or(z.literal('')),
  addressMunicipality: z.string().max(100).optional().or(z.literal('')),
  addressCity: z.string().max(100).optional().or(z.literal('')),
  addressStreet: z.string().max(255).optional().or(z.literal('')),
  addressNumber: z.string().max(10).optional().or(z.literal('')),
  addressBlock: z.string().max(10).optional().or(z.literal('')),
  addressEntrance: z.string().max(10).optional().or(z.literal('')),
  addressFloor: z.string().max(10).optional().or(z.literal('')),
  addressApartment: z.string().max(10).optional().or(z.literal('')),

  leadDiagnosis: z.string().optional().or(z.literal('')),
  accompanyingConditions: z.string().optional().or(z.literal('')),
  allergies: z.string().optional().or(z.literal('')),
  therapyFromPersonalDoctor: z.string().optional().or(z.literal('')),
  conditionAtAdmission: z.string().optional().or(z.literal('')),
  actualization: z.string().optional().or(z.literal('')),
  conditionChange: z.string().optional().or(z.literal('')),
  disabilityType: z.string().max(255).optional().or(z.literal('')),
  disabilityPercentage: z
    .number({ message: 'patients:errors.invalidPercentage' })
    .int('patients:errors.invalidPercentage')
    .min(0, 'patients:errors.invalidPercentage')
    .max(100, 'patients:errors.invalidPercentage')
    .optional(),

  personalDoctorName: z.string().max(200).optional().or(z.literal('')),
  personalDoctorPhone: z.string().max(20).optional().or(z.literal('')),
  personalDoctorAddress: z.string().max(500).optional().or(z.literal('')),

  admissionDate: z.string().min(1, 'patients:errors.admissionDateRequired'),
  admissionTime: z
    .string()
    .regex(timeRegex, 'patients:errors.invalidTime')
    .optional()
    .or(z.literal('')),
  registryNumber: z.string().max(50).optional().or(z.literal('')),
  roomNumber: z.string().max(20).optional().or(z.literal('')),
  bedNumber: z.string().max(20).optional().or(z.literal('')),

  dischargeDate: z.string().optional().or(z.literal('')),
  dischargeTime: z
    .string()
    .regex(timeRegex, 'patients:errors.invalidTime')
    .optional()
    .or(z.literal('')),
  dischargeNotes: z.string().optional().or(z.literal('')),
  deceasedDate: z.string().optional().or(z.literal('')),
  deceasedTime: z
    .string()
    .regex(timeRegex, 'patients:errors.invalidTime')
    .optional()
    .or(z.literal('')),
  deceasedNotes: z.string().optional().or(z.literal('')),

  education: z.string().max(255).optional().or(z.literal('')),
  profession: z.string().max(255).optional().or(z.literal('')),
  maritalStatus: z.string().max(50).optional().or(z.literal('')),

  contractNumber: z.string().max(50).optional().or(z.literal('')),
  contractStartDate: z.string().optional().or(z.literal('')),
  contractEndDate: z.string().optional().or(z.literal('')),
  monthlyFee: z.number().min(0).optional(),

  contact1FullName: z.string().max(200).optional().or(z.literal('')),
  contact1Relationship: z.string().max(50).optional().or(z.literal('')),
  contact1Phone: z.string().max(20).optional().or(z.literal('')),
  contact1Address: z.string().max(500).optional().or(z.literal('')),
  contact2FullName: z.string().max(200).optional().or(z.literal('')),
  contact2Relationship: z.string().max(50).optional().or(z.literal('')),
  contact2Phone: z.string().max(20).optional().or(z.literal('')),
  contact2Address: z.string().max(500).optional().or(z.literal('')),

  photoVideoConsent: z.boolean(),
  restrictedContacts: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
})

export type PatientFormValues = z.infer<typeof patientFormSchema>

export const defaultPatientValues: PatientFormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  egn: '',
  dateOfBirth: '',
  gender: 'MALE',
  placeOfBirth: '',

  idCardNumber: '',
  idCardIssuedBy: '',
  idCardIssuedDate: '',

  phone: '',
  address: '',
  addressOblast: '',
  addressMunicipality: '',
  addressCity: '',
  addressStreet: '',
  addressNumber: '',
  addressBlock: '',
  addressEntrance: '',
  addressFloor: '',
  addressApartment: '',

  leadDiagnosis: '',
  accompanyingConditions: '',
  allergies: '',
  therapyFromPersonalDoctor: '',
  conditionAtAdmission: '',
  actualization: '',
  conditionChange: '',
  disabilityType: '',
  disabilityPercentage: undefined,

  personalDoctorName: '',
  personalDoctorPhone: '',
  personalDoctorAddress: '',

  admissionDate: new Date().toISOString().split('T')[0],
  admissionTime: '',
  registryNumber: '',
  roomNumber: '',
  bedNumber: '',

  dischargeDate: '',
  dischargeTime: '',
  dischargeNotes: '',
  deceasedDate: '',
  deceasedTime: '',
  deceasedNotes: '',

  education: '',
  profession: '',
  maritalStatus: '',

  contractNumber: '',
  contractStartDate: '',
  contractEndDate: '',
  monthlyFee: undefined,

  contact1FullName: '',
  contact1Relationship: '',
  contact1Phone: '',
  contact1Address: '',
  contact2FullName: '',
  contact2Relationship: '',
  contact2Phone: '',
  contact2Address: '',

  photoVideoConsent: false,
  restrictedContacts: '',
  notes: '',
}
